// п. 20 (ФЗ-152, РГ-01/РГ-08). Фронтовая половина активации приглашения: тексты
// и правило повтора — то, что можно проверить без браузера.

/**
 * Ответ на запрос кода **один и тот же** при любом ответе сервера 2xx: и когда
 * адрес внесён, и когда нет. Иначе форма стала бы способом проверить, есть ли
 * учётная запись (`РГ-01`).
 */
// ВХОД.2 (`oq-ММБ-344`): в ответе — адрес, который ушёл в запрос. Его человек
// ввёл (или получил из письма) сам, поэтому внесённость адреса текст по-прежнему
// не раскрывает: при любом 2xx он одинаков, кроме подставленного адреса.
export function activationRequestAnswer(email: string): string {
  return (
    `Код отправлен на ${email.trim()}. Нет письма 5 минут — проверьте адрес ` +
    "и папку „Спам“ или отправьте ещё раз"
  );
}

/**
 * ВХОД.2: адрес из ссылки письма-приглашения (`/activation#email=…`).
 * Фрагмент разбирается как параметры; закодированный плюс (`%2B`) остаётся
 * плюсом. Нет параметра или он пуст — пустая строка.
 */
export function emailFromHash(hash: string): string {
  const params = new URLSearchParams(String(hash || "").replace(/^#/, ""));

  return (params.get("email") || "").trim();
}

/**
 * ВХОД.1: запрос кода или ссылки не дошёл до обработки — отказ (кроме 429) или
 * обрыв связи. Прежде форма и здесь писала «код отправлен», и человек ждал
 * письма, которого не будет. От адреса 403 и обрыв не зависят, поэтому
 * внесённость адреса этот текст не раскрывает.
 */
export const REQUEST_NOT_SENT_MESSAGE =
  "Запрос не отправлен. Обновите страницу и попробуйте ещё раз.";

/**
 * Сколько ждать между повторами запроса кода. Сервер считает своё окно
 * (`RATE_LIMIT_ACTIVATION_*`) и остаётся источником истины; отсчёт на кнопке —
 * нижняя граница, чтобы человек не жал «отправить» подряд и не получал 429.
 */
export const ACTIVATION_COOLDOWN_SECONDS = 60;

/**
 * `20.1`: сколько ждать по ответу сервера. Заголовок `Retry-After` — тот самый
 * способ, которым сервер говорит «когда можно снова»; пока его не читали, экран
 * называл своё число (`ACTIVATION_COOLDOWN_SECONDS`), и человек мог жать кнопку
 * раньше срока или ждать дольше, чем нужно.
 *
 * Заголовок бывает двух видов по стандарту: число секунд и дата. Оба понимаем —
 * иначе на сервере, который отвечает датой, отсчёт снова стал бы догадкой.
 * Тело ответа (`retryAfter`) принимаем как запасной вид: заголовок иногда теряют
 * посредники.
 *
 * @returns секунды (целые, не меньше 1) или `null`, если сервер времени не назвал
 */
export function retryAfterSeconds(error: any, now: number = Date.now()): number | null {
  const header = error?.response?.headers?.["retry-after"] ?? error?.response?.headers?.["Retry-After"];
  const body = error?.response?.data?.retryAfter ?? error?.response?.data?.retry_after;
  const value = header ?? body;

  if (value === undefined || value === null || value === "") return null;

  // Число секунд — самый частый вид.
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 0 ? Math.ceil(value) : null;
  }

  const text = String(value).trim();

  if (/^\d+$/.test(text)) {
    const seconds = Number(text);

    return seconds > 0 ? seconds : null;
  }

  // Дата: сколько секунд осталось до неё. Прошедшая дата — ждать нечего.
  const at = Date.parse(text);

  if (Number.isNaN(at)) return null;

  const seconds = Math.ceil((at - now) / 1000);

  return seconds > 0 ? seconds : null;
}

/** Слова для ожидания: минуты — когда их хотя бы одна, иначе секунды. */
export function waitSecondsText(seconds: number): string {
  if (seconds >= 60) {
    const minutes = Math.round(seconds / 60);

    return `${minutes} мин`;
  }

  return `${seconds} с`;
}

/**
 * Отказ по частоте. Время берём из ответа сервера, когда он его назвал
 * (`20.1`), иначе — своё: без числа ждать наугад хуже, чем ждать известное.
 */
export function rateLimitedMessage(seconds: number): string {
  return `слишком часто, повторите через ${waitSecondsText(seconds)}`;
}

/**
 * Код не подошёл. Сервер отвечает одним общим текстом (`РГ-01`: ответ не должен
 * отличать внесённый адрес от прочего), поэтому **свой** текст здесь только
 * запасной — на случай связи без тела ответа.
 */
export const ACTIVATION_CODE_FAILED_FALLBACK = "код не подошёл — проверьте и повторите";

/**
 * `21.1` (`АУД-Н178`, `ПРГ-В3`): постоянная подсказка под полем кода. Показывается
 * при **любом** отказе `verify` — и когда сервер назвал свой текст, и когда
 * ответа нет. Счётчика попыток нет (решение 19.10): подсказка одна и та же.
 */
export const ACTIVATION_CODE_HINT =
  "проверьте код из письма или запросите новый";

/** Правило пароля то же, что на сервере в `POST /activation/complete`. */
export const ACTIVATION_PASSWORD_MIN_LENGTH = 8;

export const ACTIVATION_PASSWORD_RULES_MESSAGE =
  `пароль от ${ACTIVATION_PASSWORD_MIN_LENGTH} знаков`;

export const ACTIVATION_PERSONAL_REQUIRED_MESSAGE = "нужны имя и фамилия";

export const ACTIVATION_CONSENT_REQUIRED_MESSAGE =
  "без отметки согласия активация невозможна";


/** Код письма — ровно 6 цифр (макет, шаг 2). */
export const ACTIVATION_CODE_LENGTH = 6;

/** Пароль и повтор не совпали — до запроса, чтобы не тратить попытку. */
export const ACTIVATION_PASSWORD_MISMATCH_MESSAGE = "пароли не совпадают";
